import React, { Key } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IconBackground, ThreeDotMenu, UserRoleChip } from 'common/components';
import { EColor } from 'common/const';
import { dateFormatter, generateTreeNodeKeyAndSort, getEmail, priceFormatter } from 'common/helpers';
import { ITreeDataNode, IUserShort } from 'common/models';
import {
  ArrowRightShort,
  Bin,
  Contract,
  Delivery,
  Discount,
  Edit,
  InfoRed,
  InfoYellow,
  LegalEntity,
  Options,
  Plus,
} from 'common/svg';
import { IAddress } from 'entities/Address/Address.models';
import { ICatalog } from 'entities/Catalog/Catalog.models';
import { ICategory } from 'entities/Category/Category.models';
import { IDiscountBrand } from 'entities/Discount/Discount.models';
import { ICreateInvitationPayload } from 'entities/Invitation/Invitation.models';
import { ILegal } from 'entities/LegalEntity/LegalEntity.models';
import { ESettingsMenuTab } from 'entities/Settings/Settings.enum';
import { IStock } from 'entities/Stock/Stock.models';
import { ISubdivision, ISubdivisionAddress, ISubdivisionItem } from 'entities/Subdivision/Subdivision.models';
import { IUser } from 'entities/User/User.models';
import { calculateUserBalance, getUserName, getUserRole } from 'entities/User/User.helper';
import { EUserRole, EUserStatus } from 'entities/User/User.enum';
import { IContract } from 'entities/Contract/Contract.models';

export const renderCatalogListSettingsRecords = (
  handleUpdateCatalogClick: (record: ICatalog) => void,
  handleDeleteCatalogClick: (catalogId: number) => void,
) => {
  return [
    {
      title: 'Название каталога',
      dataIndex: 'name',
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      render: (text) => dateFormatter('DD MMMM YYYY', text),
      width: 200,
    },
    {
      title: 'Изменен',
      dataIndex: 'updatedAt',
      render: (text) => dateFormatter('DD MMMM YYYY', text),
      width: 200,
    },
    {
      title: 'Автор',
    },
    {
      title: '',
      render: (_, record) => (
        <ThreeDotMenu
          items={[
            {
              key: 'edit',
              label: (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateCatalogClick(record);
                  }}
                >
                  Редактировать
                </Button>
              ),
              className: 'edit',
            },
            {
              key: 'delete',
              label: (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCatalogClick(record.id);
                  }}
                >
                  Удалить
                </Button>
              ),
              className: 'delete',
            },
          ]}
        />
      ),
      width: 64,
    },
  ] as ColumnsType<ICatalog>;
};

export const mapSubdivisionListToTreeData = (
  subdivisionList: ISubdivisionItem[],
  onCreateClick: (key: Key) => Promise<void>,
  onTransferClick: (key: Key) => void,
  onDeleteClick: (id: Key) => void,
): ITreeDataNode[] => {
  const map = new Map<number, ITreeDataNode>();
  const roots: ITreeDataNode[] = [];

  subdivisionList.forEach(({ id, name }) => {
    map.set(id, {
      key: String(id),
      title: name,
      children: [],
      icon: (props) => {
        return (
          <div className="settings-sidebar__subdivision-tree-item-container">
            <Dropdown
              overlayClassName="redesign"
              menu={{
                items: [
                  {
                    key: 'transfer',
                    label: 'Перенести в...',
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      onTransferClick(props.data.key);
                    },
                  },
                  { type: 'divider' },
                  {
                    key: 'delete',
                    label: 'Удалить подразделение',
                    className: 'red',
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      onDeleteClick(props.data.key);
                    },
                  },
                ],
              }}
            >
              <Button className="button-icon" icon={<Options className="icon-options-dark-grey" />} />
            </Dropdown>

            <Tooltip title="Дочернее подразделение" placement="bottom">
              <Button
                className="button-icon"
                icon={<Plus className="icon-plus-dark-grey" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateClick(props.data.key);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    });
  });

  subdivisionList.forEach(({ id, parentId }) => {
    const node = map.get(id);

    if (node) {
      if (parentId !== undefined && map.has(parentId)) {
        const parent = map.get(parentId);

        parent?.children?.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  generateTreeNodeKeyAndSort(roots);

  return roots;
};

export const mapCategoryListToTreeData = (categoryList: ICategory[]): ITreeDataNode[] => {
  const map = new Map<number, ITreeDataNode>();
  const roots: ITreeDataNode[] = [];

  categoryList.forEach(({ id, displayName }) => {
    map.set(id, {
      key: String(id),
      title: displayName,
      children: [],
    });
  });

  categoryList.forEach(({ id, parentIds }) => {
    const node = map.get(id);

    if (node) {
      const parentId = parentIds?.[parentIds.length - 1];

      if (parentId !== undefined && map.has(parentId)) {
        const parent = map.get(parentId);

        parent?.children?.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  generateTreeNodeKeyAndSort(roots);

  return roots;
};

export const buildTreeKeysFromCategoryList = (categoryList: ICategory[], parentId: number | null, prefix: string = ''): Key[] => {
  const result: Key[] = [];

  categoryList.forEach((category) => {
    const isRootCategory = parentId === null && (!category.parentIds || category.parentIds.length === 0);

    const isChildCategory = parentId !== null && category.parentIds?.includes(parentId);

    if (isRootCategory || isChildCategory) {
      const key = prefix ? `${prefix}-${category.id}` : `${category.id}`;

      result.push(key);

      result.push(...buildTreeKeysFromCategoryList(categoryList, category.id, key));
    }
  });

  return result;
};

export const getSubdivisionTreeKeysBySubdivisionId = (subdivisionList: ISubdivisionItem[], id?: string): string[] => {
  if (!id || isNaN(Number(id))) {
    return [];
  }

  const dataMap = new Map<number, ISubdivisionItem>(subdivisionList.map((item) => [item.id, item]));
  const result: number[] = [];

  let current: ISubdivisionItem | undefined = dataMap.get(Number(id));

  while (current) {
    result.unshift(current.id);
    current = current.parentId ? dataMap.get(current.parentId) : undefined;
  }

  return [result.join('-')];
};

export const getSubdivisionSettingsSubdivisionLegalEntityOptions = (legalEntityList: ILegal[]) => {
  return legalEntityList.map((legalEntity) => ({ label: legalEntity.name, value: legalEntity.id }));
};

export const getSubdivisionSettingsSubdivisionHeadOptions = (userList: IUser[]) => {
  return userList
    .filter((user) => user.roles.includes(EUserRole.HeadOfDepartment))
    .map((user) => ({ label: getUserName.fullName(user.lastName, user.firstName, user.patronymic), value: user.id }));
};

export const getSubdivisionSettingsManagerOptions = (userList: IUser[]) => {
  return userList
    .filter((user) => user.roles.includes(EUserRole.Manager))
    .map((user) => ({ label: getUserName.fullName(user.lastName, user.firstName, user.patronymic), value: user.id }));
};

export const getSubdivisionSettingsBreadcrumbItems = (
  subdivisionList: ISubdivisionItem[],
  selectedTreeKeys: string[],
  onClick?: (id: number) => void,
) => {
  const subdivisionIds = selectedTreeKeys[0]?.split('-').map(Number);

  if (!subdivisionIds) {
    return [];
  }

  return subdivisionIds
    .map((id) => subdivisionList.find((item) => item.id === id) as ISubdivision)
    .map((item) => ({
      title: item?.name,
      onClick: () => onClick?.(item?.id),
    }));
};

export const getAccountSettingsMenuItems = (isSeller?: boolean) => {
  return isSeller
    ? [
        { key: ESettingsMenuTab.UserList, label: 'Пользователи' },
        { key: ESettingsMenuTab.StockList, label: 'Склады отгрузки' },
        { key: ESettingsMenuTab.Integrations, label: 'Интеграции' },
      ]
    : [
        { key: ESettingsMenuTab.Contracts, label: 'Контракты' },
        { key: ESettingsMenuTab.PersonalLimits, label: 'Персональные лимиты' },
        { key: ESettingsMenuTab.UserList, label: 'Пользователи' },
        { key: ESettingsMenuTab.LegalEntityList, label: 'Юридические лица' },
        { key: ESettingsMenuTab.AddressList, label: 'Адреса доставки' },
      ];
};

export const renderUserListSettingsRecords = (resendInvite?: (payload: ICreateInvitationPayload) => void, isSeller?: boolean) => {
  return [
    {
      key: 'abbreviation',
      render: (_, { firstName, lastName, status }) => {
        if (status === EUserStatus.NotActive) {
          return <IconBackground content={<InfoRed className="icon-info-red" />} color={EColor.PaleRed} />;
        }

        return (
          <IconBackground
            content={<span className="text-profile-icon color-bright-green">{getUserName.initials(lastName, firstName)}</span>}
            color={EColor.PaleGreen}
          />
        );
      },
      width: 40,
    },
    {
      key: 'name',
      render: (_, { firstName, lastName, patronymic, email }) => {
        return (
          <div>
            <div className="text-controls-accent mb-2">{getUserName.fullName(lastName, firstName, patronymic)}</div>

            <span className="text-tag color-dark-grey">{getEmail(email)}</span>
          </div>
        );
      },
    },
    {
      key: 'role',
      render: (_, { roles }) => {
        const { isAccountAdmin } = getUserRole(roles);

        if (!isAccountAdmin) {
          return null;
        }

        return <UserRoleChip color={EColor.PalePurple} name="Администратор аккаунта" />;
      },
    },
    {
      key: 'subdivision',
      render: (_, { email, firstName, lastName, patronymic, subdivisions, status }) => {
        if (status === EUserStatus.NotActive && resendInvite && email) {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                className="button-sm secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  resendInvite({ email, firstName, lastName, patronymic });
                }}
              >
                Пригласить повторно
              </Button>
            </div>
          );
        }

        if (isSeller || subdivisions?.length) {
          return null;
        }

        return (
          <div className="account-user-list-settings__table-item-subdivision">
            <InfoYellow className="icon-info-yellow" />

            <span className="text-tag-accent">Подразделение не назначено</span>
          </div>
        );
      },
      width: 239,
    },
  ] as ColumnsType<IUser>;
};

export const renderLegalEntityListSettingsRecords = (
  onEditClick: (id: number) => void,
  onDeleteClick: (record: ILegal) => void,
) => {
  return [
    {
      key: 'name',
      render: (_, { name, inn, isVatPayer }) => {
        return (
          <div className="account-legal-entity-list-settings__table-item-name">
            <IconBackground content={<LegalEntity className="icon-legal-entity-bright-green" />} color={EColor.PaleGreen} />

            <div>
              <div className="text-controls-accent mb-2">{name}</div>

              <span className="text-tag color-dark-grey">{inn}</span>

              <div className="text-tag color-dark-grey">{isVatPayer ? 'Плательщик НДС' : 'Не плательщик НДС'}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Dropdown
            overlayClassName="redesign"
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Редактировать',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onEditClick(record.id);
                  },
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Удалить',
                  className: 'red',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onDeleteClick(record);
                  },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="button-icon"
              icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
            />
          </Dropdown>
        );
      },
      width: 28,
    },
  ] as ColumnsType<ILegal>;
};

export const renderSettingsAccountAddressesRecord = (
  onEdit: (record: IAddress) => void,
  onDelete: (record: IAddress) => void,
) => {
  return [
    {
      key: 'icon',
      render: () => {
        return <IconBackground content={<Delivery className="icon-delivery-bright-green" />} color={EColor.PaleGreen} />;
      },
      width: 40,
    },
    {
      key: 'name',
      render: (_, { name }) => {
        return <div className="text-controls-accent">{name}</div>;
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Dropdown
            overlayClassName="redesign"
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Редактировать',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onEdit(record);
                  },
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Удалить адрес',
                  className: 'red',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onDelete(record);
                  },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="button-icon"
              icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
            />
          </Dropdown>
        );
      },
      width: 28,
    },
  ] as ColumnsType<IAddress>;
};

export const renderSettingsSubdivisionAddressesRecord = (
  onEdit: (record: IAddress) => void,
  onDelete: (record: IAddress) => void,
) => {
  return [
    {
      key: 'icon',
      render: () => {
        return <IconBackground content={<Delivery className="icon-delivery-bright-green" />} color={EColor.PaleGreen} />;
      },
      width: 40,
    },
    {
      key: 'name',
      render: (_, { name, isMain }) => {
        return (
          <div>
            <div className="text-controls-accent mb-2">{name}</div>

            {isMain && <span className="text-tag color-dark-grey">Основной адрес</span>}
          </div>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Dropdown
            overlayClassName="redesign"
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Сделать основным',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onEdit(record);
                  },
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Отвязать адрес',
                  className: 'red',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onDelete(record);
                  },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="button-icon"
              icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
            />
          </Dropdown>
        );
      },
      width: 28,
    },
  ] as ColumnsType<ISubdivisionAddress>;
};

export const renderStockListSettingsRecords = (onEdit: (id: number) => void, onDelete: (id: number) => void) => {
  return [
    {
      key: 'icon',
      render: () => {
        return <IconBackground content={<Delivery className="icon-delivery-bright-green" />} color={EColor.PaleGreen} />;
      },
      width: 40,
    },
    {
      key: 'name',
      render: (_, { name, address }) => {
        return (
          <div>
            <div className="text-controls-accent mb-2">{name}</div>

            <span className="text-tag color-dark-grey">{address}</span>
          </div>
        );
      },
    },
    {
      key: 'actions',
      render: (_, { id }) => {
        return (
          <Dropdown
            overlayClassName="redesign"
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Редактировать',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onEdit(id);
                  },
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Удалить склад',
                  className: 'red',
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    onDelete(id);
                  },
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="button-icon"
              icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
            />
          </Dropdown>
        );
      },
      width: 28,
    },
  ] as ColumnsType<IStock>;
};

export const renderContractGeneralSettingsUserListRecords = (isEditable: boolean, onDelete: (id: number) => void) => {
  return [
    {
      key: 'name',
      render: (_, { lastName, firstName, patronymic, email }) => {
        return (
          <div className="contract-general-settings__table-item-name">
            <IconBackground
              content={<span className="text-profile-icon color-dark-grey">{getUserName.initials(lastName, firstName)}</span>}
              color={EColor.LightGrey}
            />

            <div>
              <div className="text-controls-accent mb-2">{getUserName.fullName(lastName, firstName, patronymic)}</div>

              <span className="text-tag color-dark-grey">{getEmail(email)}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'action',
      render: (_, { id }) => {
        return isEditable ? (
          <Button className="button-icon" icon={<Bin className="icon-bin-dark-grey" />} onClick={() => onDelete(id)} />
        ) : null;
      },
      width: 28,
    },
  ] as ColumnsType<IUserShort>;
};

export const renderBrandDiscountListRecords = (onEdit: (record: IDiscountBrand) => void, onDelete: (id: number) => void) => {
  return [
    {
      key: 'name',
      render: (_, { brand, discountValue }) => {
        return (
          <div className="contract-discount-list-settings__table-item-name">
            <IconBackground content={<Discount className="icon-discount-bright-green" />} color={EColor.PaleGreen} />

            <div className="text-controls-accent">{`${brand} ${discountValue}%`}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Dropdown
            overlayClassName="redesign"
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Настроить',
                  onClick: () => onEdit(record),
                },
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Удалить скидку',
                  className: 'red',
                  onClick: () => onDelete(record.id),
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="button-icon"
              icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
            />
          </Dropdown>
        );
      },
      width: 28,
    },
  ] as ColumnsType<IDiscountBrand>;
};

export const renderPersonalLimitsSettingsRecords = (onEdit: (record: IUser) => void, onDelete: (record: IUser) => void) => {
  return [
    {
      key: 'abbreviation',
      render: (_, { firstName, lastName, status }) => {
        if (status === EUserStatus.NotActive) {
          return <IconBackground content={<InfoRed className="icon-info-red" />} color={EColor.PaleRed} />;
        }

        return (
          <IconBackground
            content={<span className="ff-bold fs-xs color-bright-green">{getUserName.initials(lastName, firstName)}</span>}
            color={EColor.PaleGreen}
          />
        );
      },
      width: 40,
    },
    {
      key: 'name',
      render: (_, { firstName, lastName, patronymic, email }) => {
        return (
          <div>
            <div className="mb-2 ff-medium fs-s">{getUserName.fullName(lastName, firstName, patronymic)}</div>

            <span className="ff-regular fs-xs color-dark-grey">{getEmail(email)}</span>
          </div>
        );
      },
    },
    {
      key: 'limit',
      render: (_, record) => {
        const balance = calculateUserBalance(record);

        if (!balance) {
          return null;
        }

        return (
          <div>
            <div className="mb-2 ff-medium fs-s">{priceFormatter(balance)}</div>

            <span className="ff-regular fs-xs color-dark-grey">остаток за период</span>
          </div>
        );
      },
      onCell: () => {
        return { style: { textAlign: 'end' } };
      },
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <div className="flex align-center gap-6">
            <Button
              className="button-icon"
              icon={<Edit className="icon-edit-dark-grey" />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
            />
            <Button
              className="button-icon"
              icon={<Bin className="icon-bin-pale-red" />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(record);
              }}
            />
          </div>
        );
      },
      width: 76,
    },
  ] as ColumnsType<IUser>;
};

export const renderSettingsIntegrationsBuyerListRecords = () => {
  return [
    {
      key: 'buyerName',
      render: (_, record) => {
        return (
          <div className="flex align-center">
            <div className="mr-6">
              <IconBackground content={<Contract className="icon-contract-bright-green" />} color={EColor.PaleGreen} />
            </div>

            <div className="ff-medium fs-s flex-1">{record.buyerName}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      render: () => <ArrowRightShort className="icon-arrow-right-short-dark-grey" />,
      width: 48,
    },
  ] as ColumnsType<IContract>;
};
